/**
 * References in building-assistant answers. The model writes `{building:KEY}`,
 * `{component:BUILDING/COMPONENT}`, `{user:UID}` etc. (mf-functions ai/assistant/prompt.ts);
 * the panel renders them as live names and links.
 */

export type AssistantRefKind =
    | 'building'
    | 'component'
    | 'workOrder'
    | 'issue'
    | 'project'
    | 'lease'
    | 'tenant'
    | 'unit'
    | 'user';

export interface AssistantRef {
    kind: AssistantRefKind;
    /** Path-like keys: one for most kinds, two (building, then child) for component and unit. */
    keys: string[];
    /** The reference as written, shown as-is if it can't be resolved. */
    raw: string;
}

export type AnswerSegment = { type: 'text'; text: string } | { type: 'ref'; ref: AssistantRef };

const KEY_COUNT: Record<AssistantRefKind, number> = {
    building: 1,
    component: 2,
    workOrder: 1,
    issue: 1,
    project: 1,
    lease: 1,
    tenant: 1,
    unit: 2,
    user: 1,
};

// Firebase keys and UIDs: no `.#$[]/` and no braces or whitespace.
const REF_PATTERN = /\{(building|component|workOrder|issue|project|lease|tenant|unit|user):([^{}\s/]+(?:\/[^{}\s/]+)?)\}/g;

/** Splits text into plain text and references; malformed references stay plain text. */
export function parseAnswerReferences(text: string): AnswerSegment[] {
    const segments: AnswerSegment[] = [];
    let last = 0;
    for (const match of Array.from(text.matchAll(REF_PATTERN))) {
        const kind = match[1] as AssistantRefKind;
        const keys = match[2].split('/');
        if (keys.length !== KEY_COUNT[kind]) continue;
        const start = match.index ?? 0;
        if (start > last) segments.push({ type: 'text', text: text.slice(last, start) });
        segments.push({ type: 'ref', ref: { kind, keys, raw: match[0] } });
        last = start + match[0].length;
    }
    if (last < text.length) segments.push({ type: 'text', text: text.slice(last) });
    return segments;
}
